/**
 * Centralized slug definitions for Raisa V2
 * All slugs use snake_case for consistency with backend enums
 */

// =============================================================================
// TRAVEL STYLES
// =============================================================================

export const TRAVEL_STYLES = {
  RELAX: 'relax',
  ADVENTURE: 'adventure',
  CULTURE: 'culture',
  FOODIE: 'foodie',
  LUXURY: 'luxury',
} as const;

export type TravelStyleSlug = typeof TRAVEL_STYLES[keyof typeof TRAVEL_STYLES];

export const TRAVEL_STYLE_LABELS: Record<TravelStyleSlug, string> = {
  [TRAVEL_STYLES.RELAX]: 'tripBuilder.style_relax',
  [TRAVEL_STYLES.ADVENTURE]: 'tripBuilder.style_adventure',
  [TRAVEL_STYLES.CULTURE]: 'tripBuilder.style_culture',
  [TRAVEL_STYLES.FOODIE]: 'tripBuilder.style_foodie',
  [TRAVEL_STYLES.LUXURY]: 'tripBuilder.style_luxury',
};

export const TRAVEL_STYLE_LIST = Object.values(TRAVEL_STYLES);

// =============================================================================
// TRIP VISIBILITY
// =============================================================================

export const VISIBILITY_TYPES = {
  PRIVATE: 'private',
  UNLISTED: 'unlisted',           // Default: public by link, noindex
  PUBLIC_INDEXED: 'public_indexed', // Admin-only: SEO indexed
} as const;

export type VisibilityType = typeof VISIBILITY_TYPES[keyof typeof VISIBILITY_TYPES];

export const VISIBILITY_LABELS: Record<VisibilityType, string> = {
  [VISIBILITY_TYPES.PRIVATE]: 'visibility.private',
  [VISIBILITY_TYPES.UNLISTED]: 'visibility.unlisted',
  [VISIBILITY_TYPES.PUBLIC_INDEXED]: 'visibility.public_indexed',
};

// =============================================================================
// BLOCK TYPES (Activity types in itinerary)
// =============================================================================

export const BLOCK_TYPES = {
  ACTIVITY: 'ACTIVITY',
  SYSTEM_ARRIVAL: 'SYSTEM_ARRIVAL',
  SYSTEM_CHECKIN: 'SYSTEM_CHECKIN',
  SYSTEM_WAKEUP: 'SYSTEM_WAKEUP',
  SYSTEM_RETURN: 'SYSTEM_RETURN',
  SYSTEM_CHECKOUT: 'SYSTEM_CHECKOUT',
  SYSTEM_DEPARTURE: 'SYSTEM_DEPARTURE',
} as const;

export type BlockType = typeof BLOCK_TYPES[keyof typeof BLOCK_TYPES];

export const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  [BLOCK_TYPES.ACTIVITY]: 'blockType.activity',
  [BLOCK_TYPES.SYSTEM_ARRIVAL]: 'trip.system_arrival',
  [BLOCK_TYPES.SYSTEM_CHECKIN]: 'trip.system_checkin',
  [BLOCK_TYPES.SYSTEM_WAKEUP]: 'trip.system_wakeup',
  [BLOCK_TYPES.SYSTEM_RETURN]: 'trip.system_return',
  [BLOCK_TYPES.SYSTEM_CHECKOUT]: 'trip.system_checkout',
  [BLOCK_TYPES.SYSTEM_DEPARTURE]: 'trip.system_departure',
};

// =============================================================================
// SEO PAGE STATUS
// =============================================================================

export const SEO_PAGE_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;

export type SeoPageStatus = typeof SEO_PAGE_STATUS[keyof typeof SEO_PAGE_STATUS];

// =============================================================================
// PATCH QUESTION TYPES (for trip confirmation flow)
// =============================================================================

export const PATCH_QUESTION_TYPES = {
  PACE: 'pace',
  BUDGET: 'budget',
  RESTAURANTS: 'restaurants',
  MOBILITY: 'mobility',
  KIDS_FRIENDLY: 'kids_friendly',
  MUST_SEE: 'must_see',
} as const;

export type PatchQuestionType = typeof PATCH_QUESTION_TYPES[keyof typeof PATCH_QUESTION_TYPES];

export const PATCH_QUESTION_LABELS: Record<PatchQuestionType, { ro: string; en: string }> = {
  [PATCH_QUESTION_TYPES.PACE]: {
    ro: 'Ritm lejer sau intens?',
    en: 'Relaxed or intense pace?',
  },
  [PATCH_QUESTION_TYPES.BUDGET]: {
    ro: 'Buget aproximativ per zi?',
    en: 'Approximate daily budget?',
  },
  [PATCH_QUESTION_TYPES.RESTAURANTS]: {
    ro: 'Să includem restaurante?',
    en: 'Should we include restaurants?',
  },
  [PATCH_QUESTION_TYPES.MOBILITY]: {
    ro: 'Transport în oraș?',
    en: 'City transportation preference?',
  },
  [PATCH_QUESTION_TYPES.KIDS_FRIENDLY]: {
    ro: 'Activități family-friendly?',
    en: 'Family-friendly activities?',
  },
  [PATCH_QUESTION_TYPES.MUST_SEE]: {
    ro: 'Obiective celebre sau ascunse?',
    en: 'Famous landmarks or hidden gems?',
  },
};

// =============================================================================
// ARRIVAL LOCATION TYPES
// =============================================================================

export const ARRIVAL_LOCATION_TYPES = {
  AIRPORT: 'AIRPORT',
  TRAIN: 'TRAIN',
  CAR: 'CAR',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ArrivalLocationType = typeof ARRIVAL_LOCATION_TYPES[keyof typeof ARRIVAL_LOCATION_TYPES];

// =============================================================================
// VACATION TYPES
// =============================================================================

export const VACATION_TYPES = {
  VACATION: 'vacation',
  CITY_BREAK: 'city_break',
  ROAD_TRIP: 'road_trip',
  WORKATION: 'workation',
} as const;

export type VacationType = typeof VACATION_TYPES[keyof typeof VACATION_TYPES];

export const VACATION_TYPE_LABELS: Record<VacationType, string> = {
  [VACATION_TYPES.VACATION]: 'calendar.type_vacation',
  [VACATION_TYPES.CITY_BREAK]: 'calendar.type_city_break',
  [VACATION_TYPES.ROAD_TRIP]: 'calendar.type_road_trip',
  [VACATION_TYPES.WORKATION]: 'calendar.type_workation',
};

// =============================================================================
// VACATION STATUS
// =============================================================================

export const VACATION_STATUS = {
  PLANNED: 'planned',
  CONFIRMED: 'confirmed',
  FINISHED: 'finished',
} as const;

export type VacationStatus = typeof VACATION_STATUS[keyof typeof VACATION_STATUS];

export const VACATION_STATUS_LABELS: Record<VacationStatus, string> = {
  [VACATION_STATUS.PLANNED]: 'calendar.status_planned',
  [VACATION_STATUS.CONFIRMED]: 'calendar.status_confirmed',
  [VACATION_STATUS.FINISHED]: 'calendar.status_finished',
};

